import { DialogTitle } from '@headlessui/react'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNotificationContext } from '../../context'
import { query } from '../../graphql'
import {
  SAMPLE_DATA_AVAILABILITY,
  getSampleDataErrorKey,
  isSampleSpace,
  partitionBySample
} from '../../helpers/sample-data'
import {
  useRemoveSampleData,
  useSampleDataAccess
} from '../../hooks/use-sample-data'
import Button from '../tailwind/Button'
import Modal from '../tailwind/Modal'

const DeleteSpacesAction = ({ spaces, updateHandler, open, setOpen }) => {
  const { t } = useTranslation()
  const { success, error } = useNotificationContext()
  const { gate } = useSampleDataAccess()
  const { mutateAsync: removeSampleData } = useRemoveSampleData()

  const [isLoading, setIsLoading] = useState(false)

  // Beispieltalente werden vollständig gelöscht statt an den Aivy-Partner
  // umgehängt. Im Discover-Onboarding bleiben sie stehen.
  const { sample, real } = partitionBySample(spaces, isSampleSpace)
  const isSampleLocked = gate === SAMPLE_DATA_AVAILABILITY.ONBOARDING
  const removableSample = isSampleLocked ? [] : sample
  const count = real.length + removableSample.length

  const deleteSpaces = async () => {
    setIsLoading(true)

    try {
      for (const space of real) {
        const { id: space_id } = space

        await query({
          query: 'spaceControl',
          variables: { action: 'deleteSpaceConnectionToPartner', space_id }
        })
      }

      if (removableSample.length) {
        try {
          await removeSampleData({
            scope: { space_ids: removableSample.map(({ id }) => id) },
            source: 'recover_delete'
          })
        } catch (err) {
          const errorKey = getSampleDataErrorKey(err)
          if (errorKey) error(t(errorKey))

          // Gab es echte Talente, sind die schon gelöscht. Dann neu laden und
          // schließen, sonst bleibt der Dialog für einen neuen Versuch offen.
          if (real.length) {
            await updateHandler()
            setOpen(false)
          }
          return
        }
      }

      await updateHandler()

      success(t('delete_spaces_action.success'))
      setIsLoading(false)
      setOpen(false)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal open={open} setOpen={() => setOpen(false)} size='xl'>
      <div className='p-4'>
        <DialogTitle className='text-center text-2xl font-bold text-gray-900'>
          {t('delete_spaces_action.title')}
        </DialogTitle>
        <span className='mt-3 block text-center text-base text-gray-900'>
          {t('delete_spaces_action.description')}
        </span>
        {sample.length > 0 && (
          <p className='mt-3 text-center text-sm text-gray-600'>
            {isSampleLocked
              ? t('sample_data.info.onboarding')
              : t('sample_data.recover.mixed_hint', { count: sample.length })}
          </p>
        )}
        <div className='mt-8 flex flex-row gap-3'>
          <Button.SecondaryLG
            fullWidth
            text={t('delete_spaces_action.cancel_action')}
            onClick={() => setOpen(false)}
          />
          <Button.PrimaryLG
            fullWidth
            text={
              count === 1
                ? t('delete_spaces_action.action_single', { count })
                : t('delete_spaces_action.action_multiple', { count })
            }
            onClick={deleteSpaces}
            isLoading={isLoading}
            disabled={count === 0}
          />
        </div>
      </div>
    </Modal>
  )
}

DeleteSpacesAction.propTypes = {
  spaces: PropTypes.array.isRequired,
  updateHandler: PropTypes.func,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
}

DeleteSpacesAction.defaultProps = {
  updateHandler: () => undefined
}

export default DeleteSpacesAction
